import { Link } from "react-router-dom";
import { Plus, Heart } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";

export const ProductCard = ({ product, className = "", ratio = "aspect-[4/5]" }) => {
  const { add } = useCart();
  const { user } = useAuth();
  const variantPrices = (product.variants || []).map((v) => v.price).filter((x) => x != null);
  const lo = variantPrices.length ? Math.min(...variantPrices) : product.price;
  const hi = variantPrices.length ? Math.max(...variantPrices) : product.price;
  const hasRange = hi - lo > 0.009;

  const wish = async () => {
    if (!user) {
      toast("Sign in to save pieces to your wishlist");
      return;
    }
    try {
      const r = await api.post("/my/wishlist", { slug: product.slug });
      toast(r.data.slugs.includes(product.slug) ? `${product.name} saved to your wishlist` : `Removed from your wishlist`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div data-testid={`product-card-${product.slug}`} className={`group ${className}`}>
      <Link to={`/product/${product.slug}`} className={`relative block overflow-hidden bg-sand ${ratio}`}>
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-charcoal/0 transition-colors duration-500 group-hover:bg-charcoal/10" />
        <button
          data-testid={`wishlist-${product.slug}`}
          onClick={(e) => { e.preventDefault(); wish(); }}
          aria-label={`Save ${product.name} to wishlist`}
          className="absolute right-3 top-3 rounded-full bg-bone/90 p-2.5 text-charcoal opacity-0 transition-all duration-300 hover:bg-moss hover:text-bone group-hover:opacity-100"
        >
          <Heart size={15} />
        </button>
      </Link>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <Link
            to={`/product/${product.slug}`}
            data-testid={`product-link-${product.slug}`}
            className="font-serif text-xl leading-tight transition-colors duration-300 hover:text-taupe"
          >
            {product.name}
          </Link>
          <p className="mt-1 text-xs uppercase tracking-[0.2em] text-ash">{product.category}</p>
        </div>
        <p data-testid={`product-price-${product.slug}`} className="whitespace-nowrap text-sm text-ash">
          {hasRange ? `${formatUSD(lo)} – ${formatUSD(hi)}` : formatUSD(product.price)}
        </p>
      </div>
      <button
        data-testid={`add-to-cart-${product.slug}`}
        onClick={() => {
          add(product);
          toast(`${product.name} added to your cart`);
        }}
        className="mt-3 flex items-center gap-2 rounded-full border border-charcoal/20 px-5 py-2 text-xs uppercase tracking-[0.2em] opacity-0 transition-all duration-300 hover:bg-charcoal hover:text-bone group-hover:opacity-100"
      >
        <Plus size={13} /> Add to cart
      </button>
    </div>
  );
};
